import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import {ReportChart} from '../../../reports/report-chart';
import {ReportDataSummary} from '../../../reports/report-data-summary';
import {ENGAGEMENT_SALES} from '../../../reports/report-types';
import type {ChartReportParams, ReportURLParams} from '../../../reports/report-types';

import * as actions from './sales-actions';
import {EngagementSalesReportProvider} from './sales-report-provider';

type Props = {
    activeFilters: FilterObject[];
    onChartClick?: (filters: FilterObject) => void | null | undefined;
    onParamChange: (arg1: any, arg2: boolean) => void;
    reportParams: ChartReportParams;
    reportDataSummary?: React.ReactNode;
    headerExtraContent?: React.ReactNode;
    height?: number;
    containerHeight?: number;
    urlParams: ReportURLParams<'leads'>;
    currentGapIndex: number;
};

export function SalesChart(props: Props) {
    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportParams: ChartReportParams, filters: Array<FilterObject>) => {
            dispatch(
                actions.requestReportData({
                    reportParams,
                    filters,
                })
            );
        },
        [dispatch]
    );

    const fetchListData = React.useCallback(
        (filters: Array<FilterObject>) => {
            dispatch(
                actions.requestListData({
                    filters,
                    pageNum: 1,
                    columns: [],
                    sort: '-closedTime',
                })
            );
        },
        [dispatch]
    );

    const activeFiltersString = JSON.stringify(props.urlParams.filter);
    const reportParamsString = JSON.stringify(props.reportParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedActiveFilters = React.useMemo(() => props.activeFilters, [activeFiltersString]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedReportParams = React.useMemo(() => props.reportParams, [reportParamsString]);

    React.useEffect(() => {
        fetchReportData(memoizedReportParams, memoizedActiveFilters);
        fetchListData(memoizedActiveFilters);
    }, [fetchReportData, fetchListData, memoizedReportParams, memoizedActiveFilters]);

    const reportDataSummary = (
        <ReportDataSummary
            currentGapIndex={props.currentGapIndex}
            filterParams={props.urlParams.filter}
            reportParams={memoizedReportParams}
            reportKey={ENGAGEMENT_SALES}
        />
    );

    return (
        <EngagementSalesReportProvider>
            <ReportChart
                {...props}
                reportParams={{...memoizedReportParams, facet: 'total'}}
                reportKey={ENGAGEMENT_SALES}
                hasBorder={false}
                isFullWidth={true}
                reportDataSummary={reportDataSummary}
            />
        </EngagementSalesReportProvider>
    );
}
